import React, { Component } from "react";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.css";
import { connect } from "react-redux";
import { I18n } from "react-redux-i18n";
import TopBar from "../components/TopBar";
import { hasAccess } from "../js/authProvider";

const FRESHNESS_CONFIG = {
  newDays: 30,
  modifiedDays: 30,
  modifiedSinceOptions: [
    { value: "1day", labelKey: "admin_local.guidelines_whats_new_date_range_1day", defaultLabel: "Modified since 1 day", days: 1 },
    { value: "1week", labelKey: "admin_local.guidelines_whats_new_date_range_1week", defaultLabel: "Modified since 1 week", days: 7 },
    { value: "1month", labelKey: "admin_local.guidelines_whats_new_date_range_1month", defaultLabel: "Modified since 1 month", days: 30 },
    { value: "2months", labelKey: "admin_local.guidelines_whats_new_date_range_2months", defaultLabel: "Modified since 2 months", days: 60 },
    { value: "3months", labelKey: "admin_local.guidelines_whats_new_date_range_3months", defaultLabel: "Modified since 3 months", days: 90 },
    { value: "1year", labelKey: "admin_local.guidelines_whats_new_date_range_1year", defaultLabel: "Modified since 1 year", days: 365 }
  ]
};

const { normalizeTag, collectTags, getGuidelinesSelectPlaceholder } = require("./guidelinesTagUtils");

const DEFAULT_ENTRIES = require("./guidelinesEntries");
const { markGuidelinesSeen } = require("./guidelinesSeen");

const translateWithFallback = (key, fallback) => {
  const translated = I18n.t(key);
  return translated === key ? fallback : translated;
};

const JOURNEY_ORDER = ["orders", "kitchen_delivery", "inventory_admin", "administration", "insights"];
const MODULE_ORDER = ["sales", "kitchen", "stockmanagement", "reports", "administration", "integrations"];

class GuidelinesWhatsNew extends Component {
  getSessionFilterState = () => {
    if (typeof window === "undefined" || !window.sessionStorage) return {};

    try {
      const raw = window.sessionStorage.getItem("guidelines-whats-new-filters");
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  };

  persistFilterState = (nextState = this.state) => {
    if (typeof window === "undefined" || !window.sessionStorage) return;

    try {
      const persisted = {
        searchTerm: nextState.searchTerm || "",
        freshnessFilter: nextState.freshnessFilter || "all",
        dateRangeFilter: nextState.dateRangeFilter || "all",
        moduleFilter: Array.isArray(nextState.moduleFilter) ? nextState.moduleFilter : []
      };
      window.sessionStorage.setItem("guidelines-whats-new-filters", JSON.stringify(persisted));
    } catch (error) {
      // ignore session storage quota / availability issues
    }
  };

  state = {
    ...{
      searchTerm: "",
      freshnessFilter: "new",
      dateRangeFilter: "1week",
      moduleFilter: []
    },
    ...this.getSessionFilterState(),
    collapsedJourneys: {},
    collapsedModules: {}
  };

  isSyncingTagFilterSelect = false;

  getLanguage = () => localStorage.language || "en";

  getTextByLanguage = (entry, field, fallback = "") => {
    const language = this.getLanguage();
    const i18nField = entry[`${field}_i18n`];
    if (i18nField && typeof i18nField === "object") {
      return i18nField[language] || i18nField.en || fallback;
    }
    return entry[field] || fallback;
  };

  parseDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  formatDate = (value) => {
    const date = this.parseDate(value);
    if (!date) return "-";
    return date.toLocaleString(this.getLanguage(), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  isSafeUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    return /^(https?:)?\/\//i.test(url);
  };

  getArrayByLanguage = (entry, field) => {
    const language = this.getLanguage();
    const i18nField = entry[`${field}_i18n`];
    if (i18nField && typeof i18nField === "object") {
      const localized = i18nField[language] || i18nField.en;
      return Array.isArray(localized) ? localized : [];
    }
    return Array.isArray(entry[field]) ? entry[field] : [];
  };

  normalizeTextValue = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      const language = this.getLanguage();
      return value[language] || value.en || "";
    }
    return String(value);
  };

  normalizeBlocks = (entry) => {
    const blocks = this.getArrayByLanguage(entry, "contentBlocks");
    return blocks
      .map((block, index) => {
        const type = block?.type || "paragraph";
        return {
          id: block?.id || `${entry.id}-block-${index}`,
          type,
          text: this.normalizeTextValue(block?.text),
          title: this.normalizeTextValue(block?.title),
          caption: this.normalizeTextValue(block?.caption),
          url: block?.url,
          items: Array.isArray(block?.items) ? block.items.map(item => this.normalizeTextValue(item)).filter(Boolean) : []
        };
      })
      .filter(block => {
        if (block.type === "bullet-list") return block.items.length > 0;
        if (block.type === "image" || block.type === "video" || block.type === "embed") return this.isSafeUrl(block.url);
        return Boolean(block.text || block.title || block.caption);
      });
  };

  getFreshnessState = (entry) => {
    const created = this.parseDate(entry.createdAt);
    const modified = this.parseDate(entry.modifiedAt);
    const now = new Date();
    const referenceDate = created || modified || this.parseDate(entry.date);

    if (!referenceDate) return "stable";

    const ageDays = (now.getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24);
    const createdRecently = created && ageDays <= FRESHNESS_CONFIG.newDays;
    const modifiedRecently = modified && modified.getTime() > (created?.getTime?.() || 0) && ageDays <= FRESHNESS_CONFIG.modifiedDays;

    if (createdRecently) return "new";
    if (modifiedRecently) return "changed";
    return "stable";
  };

  getFreshnessThresholdDays = (filterValue) => {
    if (filterValue === "new") return FRESHNESS_CONFIG.newDays;
    if (filterValue === "changed") return FRESHNESS_CONFIG.modifiedDays;
    if (filterValue === "stable") return null;
    const option = FRESHNESS_CONFIG.modifiedSinceOptions.find(item => item.value === filterValue);
    return option ? option.days : null;
  };

  getDateRangeLabel = (value) => {
    if (value === "all") {
      return translateWithFallback("admin_local.guidelines_whats_new_date_range_all_time", "All time");
    }
    const option = FRESHNESS_CONFIG.modifiedSinceOptions.find(item => item.value === value);
    if (!option) return value;
    return translateWithFallback(option.labelKey, option.defaultLabel);
  };

  getRoleNames = () => {
    const roles = this.props.myStatus?.roles || [];
    return roles
      .filter(role => !role.entityType || role.entityType === "Restaurant" || String(role.role || "").toLowerCase() === "superadmin")
      .map(role => String(role.role || "").toLowerCase())
      .filter(Boolean);
  };

  isSuperAdmin = () => {
    const roles = this.props.myStatus?.roles || [];
    return roles.some(role => String(role.role || "").toLowerCase() === "superadmin");
  };

  hasRoleAccessByPermission = (roleName) => {
    const normalized = String(roleName || "").toLowerCase();
    const resourceHints = {
      admin: ["administration", "administration.*", "administration.products", "administration.products.*", "reports", "reports.*"],
      manager: ["administration", "administration.*", "stockmanagement", "stockmanagement.*", "sales", "sales.*", "reports", "reports.*"],
      cashier: ["sales", "sales.*", "orders", "orders.*"],
      superadmin: ["superadmin"]
    };

    return (resourceHints[normalized] || []).some(resource => hasAccess(resource, this.props.rbac));
  };

  normalizeEntries = () => {
    const raw = this.props.myStatus?.restaurant_settings?.["guidelines-whats-new"];
    const fromSettings = Array.isArray(raw) ? raw : raw?.entries;
    const entries = Array.isArray(fromSettings) ? fromSettings : DEFAULT_ENTRIES;

    return entries
      .map((entry, index) => ({
        id: entry.id || `guideline-${index}`,
        type: entry.type || "guideline",
        title: this.getTextByLanguage(entry, "title", "Update"),
        summary: this.getTextByLanguage(entry, "summary", ""),
        details: this.getTextByLanguage(entry, "details", ""),
        date: entry.date,
        createdAt: entry.createdAt || entry.date,
        modifiedAt: entry.modifiedAt || entry.date,
        module: entry.module || "administration",
        journey: entry.journey || "inventory_admin",
        roles: Array.isArray(entry.roles) ? entry.roles : [],
        permissions: Array.isArray(entry.permissions) ? entry.permissions : [],
        tags: Array.isArray(entry.tags) ? entry.tags : [],
        contentBlocks: this.normalizeBlocks(entry),
        linkLabel: this.getTextByLanguage(entry, "linkLabel", entry.linkLabel),
        linkUrl: entry.linkUrl
      }))
      .sort((a, b) => {
        const aDate = this.parseDate(a.modifiedAt || a.createdAt || a.date);
        const bDate = this.parseDate(b.modifiedAt || b.createdAt || b.date);
        return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
      });
  };

  entryVisibleForUser = entry => {
    if (this.isSuperAdmin()) {
      return true;
    }

    const userRoles = this.getRoleNames();
    const roleFilter = entry.roles.map(r => String(r).toLowerCase());
    const passesRoleFilter = roleFilter.length === 0 || roleFilter.some(role => userRoles.includes(role) || this.hasRoleAccessByPermission(role));
    const passesPermissionFilter = entry.permissions.length === 0 || entry.permissions.some(permission => hasAccess(permission, this.props.rbac));
    return passesRoleFilter && passesPermissionFilter;
  };

  filterEntries = (entries) => {
    const searchTerm = (this.state.searchTerm || "").trim().toLowerCase();
    const freshnessFilter = this.state.freshnessFilter || "all";
    const dateRangeFilter = this.state.dateRangeFilter || "all";
    const dateRangeThresholdDays = this.getFreshnessThresholdDays(dateRangeFilter);

    return entries.filter(entry => {
      const freshness = this.getFreshnessState(entry);
      const modifiedOrCreated = this.parseDate(entry.modifiedAt || entry.createdAt || entry.date);

      let freshnessOk = true;
      if (freshnessFilter !== "all") {
        freshnessOk = freshness === freshnessFilter;
      }

      let dateRangeOk = true;
      if (dateRangeFilter !== "all") {
        if (!modifiedOrCreated) {
          dateRangeOk = false;
        } else {
          const ageDays = (Date.now() - modifiedOrCreated.getTime()) / (1000 * 60 * 60 * 24);
          dateRangeOk = ageDays <= (dateRangeThresholdDays || 0);
        }
      }

      const selectedTags = Array.isArray(this.state.moduleFilter)
        ? this.state.moduleFilter.map(normalizeTag).filter(Boolean)
        : [];
      const moduleOk = selectedTags.length === 0 || selectedTags.some(tag =>
        entry.tags.some(entryTag => normalizeTag(entryTag) === tag)
      );

      const searchTarget = [
        entry.title,
        entry.summary,
        entry.details,
        ...entry.contentBlocks.flatMap(block => [block.title, block.text, block.caption, ...(block.items || [])]),
        ...(entry.tags || [])
      ].join(" ").toLowerCase();
      const searchOk = !searchTerm || searchTarget.includes(searchTerm);

      return freshnessOk && dateRangeOk && moduleOk && searchOk;
    });
  };

  onSearchChange = (e) => this.setState({ searchTerm: e.target.value });
  onFreshnessFilterChange = (e) => {
    const nextValue = e.target.value || "all";
    this.setState({ freshnessFilter: nextValue === "stable" ? "all" : nextValue });
  };
  onDateRangeFilterChange = (e) => this.setState({ dateRangeFilter: e.target.value || "all" });

  toggleTagFilter = (tag) => {
    const normalizedTag = normalizeTag(tag);
    if (!normalizedTag) return;

    this.setState(prev => ({
      moduleFilter: prev.moduleFilter.includes(normalizedTag)
        ? prev.moduleFilter.filter(value => value !== normalizedTag)
        : [...prev.moduleFilter, normalizedTag]
    }));
  };

  syncModuleFilterFromSelect = (target) => {
    if (this.isSyncingTagFilterSelect) return;

    const selectedOptions = target && target.selectedOptions
      ? Array.from(target.selectedOptions).map(option => option.value)
      : ($(target).val() || []);

    const normalized = Array.isArray(selectedOptions)
      ? selectedOptions.map(normalizeTag).filter(Boolean)
      : (selectedOptions ? [normalizeTag(selectedOptions)] : []).filter(Boolean);

    this.setState({ moduleFilter: normalized });
  };

  onModuleFilterChange = (e) => {
    if (!e || !e.target) return;
    this.syncModuleFilterFromSelect(e.target);
  };

  toggleJourney = (journeyKey) => {
    this.setState(prev => ({
      collapsedJourneys: {
        ...prev.collapsedJourneys,
        [journeyKey]: !prev.collapsedJourneys[journeyKey]
      }
    }));
  };

  toggleModule = (journeyKey, moduleKey) => {
    const key = `${journeyKey}__${moduleKey}`;
    this.setState(prev => ({
      collapsedModules: {
        ...prev.collapsedModules,
        [key]: !prev.collapsedModules[key]
      }
    }));
  };

  getJourneyLabel = (journey) => {
    const map = {
      orders: "Orders and service",
      kitchen_delivery: "Kitchen and delivery",
      inventory_admin: "Inventory and administration",
      administration: "Administration",
      insights: "Reports and insights"
    };
    return translateWithFallback(`admin_local.guidelines_whats_new_journey_${journey}`, map[journey] || journey);
  };

  getModuleLabel = (module) => {
    const map = {
      sales: "Sales",
      kitchen: "Kitchen",
      stockmanagement: "Stock management",
      reports: "Reports",
      administration: "Administration",
      integrations: "Integrations"
    };
    return translateWithFallback(`admin_local.guidelines_whats_new_module_${module}`, map[module] || module);
  };

  getTagLabel = (tag) => {
    const normalized = normalizeTag(tag);
    const map = {
      "access-control": { en: "Access control", de: "Zugriffskontrolle", hu: "Hozzáférés-vezérlés", cn: "访问控制" },
      admin: { en: "Admin", de: "Admin", hu: "Admin", cn: "管理员" },
      administration: { en: "Administration", de: "Administration", hu: "Adminisztráció", cn: "管理" },
      alerts: { en: "Alerts", de: "Benachrichtigungen", hu: "Értesítések", cn: "提醒" },
      app: { en: "App", de: "App", hu: "Alkalmazás", cn: "应用" },
      automation: { en: "Automation", de: "Automatisierung", hu: "Automatizálás", cn: "自动化" },
      b1: { en: "B1", de: "B1", hu: "B1", cn: "B1" },
      backup: { en: "Backup", de: "Sicherung", hu: "Mentés", cn: "备份" },
      basics: { en: "Basics", de: "Grundlagen", hu: "Alapok", cn: "基础" },
      bi: { en: "BI", de: "BI", hu: "BI", cn: "BI" },
      "bill-split": { en: "Bill split", de: "Rechnungsaufteilung", hu: "Számlabontás", cn: "分账" },
      blocks: { en: "Blocks", de: "Blöcke", hu: "Blokkok", cn: "订单块" },
      booking: { en: "Booking", de: "Reservierung", hu: "Foglalás", cn: "预订" },
      branding: { en: "Branding", de: "Branding", hu: "Arculat", cn: "品牌" },
      "bulk-update": { en: "Bulk update", de: "Massenaktualisierung", hu: "Tömeges frissítés", cn: "批量更新" },
      "call-center": { en: "Call center", de: "Callcenter", hu: "Telefonos ügyfélszolgálat", cn: "呼叫中心" },
      card: { en: "Card", de: "Karte", hu: "Kártya", cn: "卡" },
      "cash-register": { en: "Cash register", de: "Kasse", hu: "Pénztárgép", cn: "收银机" },
      cashier: { en: "Cashier", de: "Kasse", hu: "Pénztár", cn: "收银" },
      "central-data": { en: "Central data", de: "Zentrale Daten", hu: "Központi adatok", cn: "中心数据" },
      change: { en: "Change", de: "Wechselgeld", hu: "Visszajáró", cn: "找零" },
      cloud: { en: "Cloud", de: "Cloud", hu: "Felhő", cn: "云" },
      communication: { en: "Communication", de: "Kommunikation", hu: "Kommunikáció", cn: "沟通" },
      comparison: { en: "Comparison", de: "Vergleich", hu: "Összehasonlítás", cn: "比较" },
      compliance: { en: "Compliance", de: "Compliance", hu: "Megfelelés", cn: "合规" },
      contactless: { en: "Contactless", de: "Kontaktlos", hu: "Kontakt nélküli", cn: "无接触" },
      counter: { en: "Counter", de: "Tresen", hu: "Pult", cn: "柜台" },
      courier: { en: "Courier", de: "Fahrer", hu: "Futár", cn: "配送员" },
      crm: { en: "CRM", de: "CRM", hu: "CRM", cn: "CRM" },
      "customer-data": { en: "Customer data", de: "Kundendaten", hu: "Ügyféladatok", cn: "客户数据" },
      "customer-experience": { en: "Customer experience", de: "Kundenerlebnis", hu: "Vendégélmény", cn: "顾客体验" },
      delivery: { en: "Delivery", de: "Lieferung", hu: "Szállítás", cn: "配送" },
      design: { en: "Design", de: "Design", hu: "Design", cn: "设计" },
      device: { en: "Device", de: "Gerät", hu: "Eszköz", cn: "设备" },
      digital: { en: "Digital", de: "Digital", hu: "Digitális", cn: "数字" },
      dispatch: { en: "Dispatch", de: "Auslieferung", hu: "Kiszállítás", cn: "配送" },
      documentation: { en: "Documentation", de: "Dokumentation", hu: "Dokumentáció", cn: "文档" },
      email: { en: "Email", de: "E-Mail", hu: "E-mail", cn: "邮件" },
      excel: { en: "Excel", de: "Excel", hu: "Excel", cn: "Excel" },
      export: { en: "Export", de: "Export", hu: "Export", cn: "导出" },
      feedback: { en: "Feedback", de: "Feedback", hu: "Visszajelzés", cn: "反馈" },
      filters: { en: "Filters", de: "Filter", hu: "Szűrők", cn: "筛选" },
      fiscal: { en: "Fiscal", de: "Fiskal", hu: "Fiskális", cn: "财税" },
      fiscat: { en: "Fiscat", de: "Fiscat", hu: "Fiscat", cn: "Fiscat" },
      franchise: { en: "Franchise", de: "Franchise", hu: "Franchise", cn: "加盟" },
      grouping: { en: "Grouping", de: "Gruppierung", hu: "Csoportosítás", cn: "分组" },
      guest: { en: "Guest", de: "Gast", hu: "Vendég", cn: "顾客" },
      "guest-call": { en: "Guest call", de: "Gästeaufruf", hu: "Vendégértesítés", cn: "顾客呼叫" },
      "guest-experience": { en: "Guest experience", de: "Gästeerlebnis", hu: "Vendégélmény", cn: "顾客体验" },
      hardware: { en: "Hardware", de: "Hardware", hu: "Hardver", cn: "硬件" },
      "hello-pay": { en: "HelloPay", de: "HelloPay", hu: "HelloPay", cn: "HelloPay" },
      import: { en: "Import", de: "Import", hu: "Import", cn: "导入" },
      integration: { en: "Integration", de: "Integration", hu: "Integráció", cn: "集成" },
      inventory: { en: "Inventory", de: "Lager", hu: "Leltár", cn: "库存" },
      invoice: { en: "Invoice", de: "Rechnung", hu: "Számla", cn: "发票" },
      kds: { en: "KDS", de: "KDS", hu: "KDS", cn: "KDS" },
      kiosk: { en: "Kiosk", de: "Kiosk", hu: "Kiosk", cn: "自助点餐机" },
      kitchen: { en: "Kitchen", de: "Küche", hu: "Konyha", cn: "后厨" },
      label: { en: "Label", de: "Etikett", hu: "Címke", cn: "标签" },
      labels: { en: "Labels", de: "Etiketten", hu: "Címkék", cn: "标签" },
      "live-status": { en: "Live status", de: "Live-Status", hu: "Élő állapot", cn: "实时状态" },
      logistics: { en: "Logistics", de: "Logistik", hu: "Logisztika", cn: "物流" },
      logo: { en: "Logo", de: "Logo", hu: "Logó", cn: "Logo" },
      loyalty: { en: "Loyalty", de: "Treue", hu: "Hűség", cn: "忠诚" },
      map: { en: "Map", de: "Karte", hu: "Térkép", cn: "地图" },
      menu: { en: "Menu", de: "Menü", hu: "Menü", cn: "菜单" },
      mobile: { en: "Mobile", de: "Mobil", hu: "Mobil", cn: "移动端" },
      "multi-location": { en: "Multi-location", de: "Mehrere Standorte", hu: "Több helyszín", cn: "多门店" },
      ntak: { en: "NTAK", de: "NTAK", hu: "NTAK", cn: "NTAK" },
      operations: { en: "Operations", de: "Betrieb", hu: "Működés", cn: "运营" },
      options: { en: "Options", de: "Optionen", hu: "Opciók", cn: "选项" },
      "order-taking": { en: "Order taking", de: "Bestellannahme", hu: "Rendelésfelvétel", cn: "接单" },
      ordering: { en: "Ordering", de: "Bestellung", hu: "Rendelés", cn: "订购" },
      orders: { en: "Orders", de: "Bestellungen", hu: "Rendelések", cn: "订单" },
      overview: { en: "Overview", de: "Übersicht", hu: "Áttekintés", cn: "概览" },
      payment: { en: "Payment", de: "Zahlung", hu: "Fizetés", cn: "支付" },
      permissions: { en: "Permissions", de: "Berechtigungen", hu: "Jogosultságok", cn: "权限" },
      phone: { en: "Phone", de: "Telefon", hu: "Telefon", cn: "电话" },
      pickup: { en: "Pickup", de: "Abholung", hu: "Elvitel", cn: "自提" },
      planning: { en: "Planning", de: "Planung", hu: "Tervezés", cn: "计划" },
      pos: { en: "POS", de: "POS", hu: "POS", cn: "POS" },
      preparation: { en: "Preparation", de: "Vorbereitung", hu: "Előkészítés", cn: "准备" },
      preview: { en: "Preview", de: "Vorschau", hu: "Előnézet", cn: "预览" },
      pricing: { en: "Pricing", de: "Preisgestaltung", hu: "Árazás", cn: "定价" },
      printing: { en: "Printing", de: "Drucken", hu: "Nyomtatás", cn: "打印" },
      product: { en: "Product", de: "Produkt", hu: "Termék", cn: "产品" },
      production: { en: "Production", de: "Produktion", hu: "Gyártás", cn: "生产" },
      profitability: { en: "Profitability", de: "Rentabilität", hu: "Jövedelmezőség", cn: "盈利能力" },
      purchasing: { en: "Purchasing", de: "Einkauf", hu: "Beszerzés", cn: "采购" },
      qr: { en: "QR", de: "QR", hu: "QR", cn: "二维码" },
      rbac: { en: "RBAC", de: "RBAC", hu: "RBAC", cn: "RBAC" },
      receiving: { en: "Receiving", de: "Wareneingang", hu: "Bevételezés", cn: "收货" },
      recipes: { en: "Recipes", de: "Rezepte", hu: "Receptek", cn: "配方" },
      recommendations: { en: "Recommendations", de: "Empfehlungen", hu: "Ajánlások", cn: "推荐" },
      refund: { en: "Refund", de: "Rückerstattung", hu: "Visszatérítés", cn: "退款" },
      regulation: { en: "Regulation", de: "Regulierung", hu: "Szabályozás", cn: "监管" },
      reporting: { en: "Reporting", de: "Berichterstattung", hu: "Jelentés", cn: "报告" },
      reservation: { en: "Reservation", de: "Reservierung", hu: "Foglalás", cn: "预订" },
      retention: { en: "Retention", de: "Bindung", hu: "Megtartás", cn: "留存" },
      reusable: { en: "Reusable", de: "Wiederverwendbar", hu: "Újrafelhasználható", cn: "可复用" },
      "role-based": { en: "Role-based", de: "Rollenbasiert", hu: "Szerepkör-alapú", cn: "基于角色" },
      roles: { en: "Roles", de: "Rollen", hu: "Szerepkörök", cn: "角色" },
      routing: { en: "Routing", de: "Routenplanung", hu: "Útvonaltervezés", cn: "路线" },
      sales: { en: "Sales", de: "Verkauf", hu: "Értékesítés", cn: "销售" },
      scale: { en: "Scale", de: "Waage", hu: "Mérleg", cn: "秤" },
      "second-screen": { en: "Second screen", de: "Zweite Anzeige", hu: "Másodkijelző", cn: "第二屏" },
      security: { en: "Security", de: "Sicherheit", hu: "Biztonság", cn: "安全" },
      "self-service": { en: "Self-service", de: "Selbstbedienung", hu: "Önkiszolgáló", cn: "自助" },
      service: { en: "Service", de: "Service", hu: "Szolgáltatás", cn: "服务" },
      settings: { en: "Settings", de: "Einstellungen", hu: "Beállítások", cn: "设置" },
      stats: { en: "Stats", de: "Statistiken", hu: "Statisztika", cn: "统计" },
      stock: { en: "Stock", de: "Lager", hu: "Készlet", cn: "库存" },
      "stock-management": { en: "Stock management", de: "Bestandsmanagement", hu: "Készletgazdálkodás", cn: "库存管理" },
      stocktaking: { en: "Stocktaking", de: "Bestandsaufnahme", hu: "Leltározás", cn: "盘点" },
      support: { en: "Support", de: "Support", hu: "Támogatás", cn: "支持" },
      "table-service": { en: "Table service", de: "Tischservice", hu: "Asztali kiszolgálás", cn: "桌位服务" },
      tables: { en: "Tables", de: "Tische", hu: "Asztalok", cn: "桌位" },
      template: { en: "Template", de: "Vorlage", hu: "Sablon", cn: "模板" },
      templates: { en: "Templates", de: "Vorlagen", hu: "Sablonok", cn: "模板" },
      terminal: { en: "Terminal", de: "Terminal", hu: "Terminál", cn: "终端" },
      tip: { en: "Tip", de: "Trinkgeld", hu: "Borravaló", cn: "小费" },
      tracking: { en: "Tracking", de: "Verfolgung", hu: "Követés", cn: "跟踪" },
      translation: { en: "Translation", de: "Übersetzung", hu: "Fordítás", cn: "翻译" },
      updates: { en: "Updates", de: "Updates", hu: "Frissítések", cn: "更新" },
      upselling: { en: "Upselling", de: "Upselling", hu: "Upselling", cn: "增值销售" },
      users: { en: "Users", de: "Benutzer", hu: "Felhasználók", cn: "用户" },
      variations: { en: "Variations", de: "Varianten", hu: "Variációk", cn: "变体" },
      waiter: { en: "Waiter", de: "Kellner", hu: "Pincér", cn: "服务员" },
      weight: { en: "Weight", de: "Gewicht", hu: "Súly", cn: "重量" },
      workflow: { en: "Workflow", de: "Workflow", hu: "Folyamat", cn: "流程" }
    };

    const language = this.getLanguage();
    const localization = map[normalized];
    if (localization && localization[language]) return localization[language];
    if (localization && localization.en) return localization.en;
    return normalized;
  };

  buildSectionGroups = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      if (!grouped[entry.journey]) grouped[entry.journey] = {};
      if (!grouped[entry.journey][entry.module]) grouped[entry.journey][entry.module] = [];
      grouped[entry.journey][entry.module].push(entry);
    });

    return JOURNEY_ORDER.filter(journey => grouped[journey])
      .map(journey => ({
        key: journey,
        title: this.getJourneyLabel(journey),
        modules: MODULE_ORDER.filter(module => grouped[journey][module])
          .map(module => ({
            key: module,
            title: this.getModuleLabel(module),
            entries: grouped[journey][module]
          }))
      }));
  };

  getModuleFilterGroups = (entries = []) => {
    const tagOptions = collectTags(entries).map(tag => ({
      value: tag,
      label: `#${this.getTagLabel(tag)}`
    }));

    return tagOptions.length > 0 ? [{
      key: "tags",
      label: "Tags",
      options: tagOptions
    }] : [];
  };

  getTagSummary = (entries = []) => {
    const counts = {};

    entries.forEach(entry => {
      const tags = Array.isArray(entry.tags) ? entry.tags : [];
      tags.forEach(tag => {
        const normalizedTag = normalizeTag(tag);
        if (!normalizedTag) return;
        counts[normalizedTag] = (counts[normalizedTag] || 0) + 1;
      });
    });

    return Object.entries(counts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag))
      .slice(0, 20);
  };

  renderBlock = (block) => {
    const commonTextStyle = { color: "#5d4f3c", marginTop: 0 };
    if (block.type === "heading") {
      return <h4 style={{ margin: "12px 0 6px 0", color: "#3e3527" }}>{block.text || block.title}</h4>;
    }
    if (block.type === "bullet-list") {
      return (
        <ul style={{ margin: "8px 0", paddingLeft: 20, color: "#5d4f3c" }}>
          {block.items.map((item, idx) => <li key={`${block.id}-${idx}`}>{item}</li>)}
        </ul>
      );
    }
    if (block.type === "image") {
      return (
        <div style={{ marginTop: 10 }}>
          <img
            src={block.url}
            alt={block.caption || block.title || "Guideline visual"}
            style={{ width: "100%", borderRadius: 10, border: "1px solid #e3d8c4", objectFit: "cover", maxHeight: 420 }}
          />
          {block.caption ? <div style={{ marginTop: 6, color: "#7a6a54", fontSize: 13 }}>{block.caption}</div> : null}
        </div>
      );
    }
    if (block.type === "video") {
      const isPlayableVideo = /\.(mp4|webm|ogg)(\?|#|$)/i.test(block.url || "");
      if (isPlayableVideo) {
        return (
          <div style={{ marginTop: 10 }}>
            <video controls style={{ width: "100%", borderRadius: 10, border: "1px solid #e3d8c4", maxHeight: 420 }}>
              <source src={block.url} />
            </video>
            {block.caption ? <div style={{ marginTop: 6, color: "#7a6a54", fontSize: 13 }}>{block.caption}</div> : null}
          </div>
        );
      }
      return (
        <div style={{ marginTop: 10 }}>
          <a href={block.url} target="_blank" rel="noopener noreferrer">{block.title || block.caption || block.url}</a>
          {block.caption ? <div style={{ marginTop: 6, color: "#7a6a54", fontSize: 13 }}>{block.caption}</div> : null}
        </div>
      );
    }
    if (block.type === "embed") {
      return (
        <div style={{ marginTop: 10 }}>
          <iframe
            title={block.title || block.caption || block.id}
            src={block.url}
            style={{ width: "100%", minHeight: 320, border: "1px solid #e3d8c4", borderRadius: 10, background: "#fff" }}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
          {block.caption ? <div style={{ marginTop: 6, color: "#7a6a54", fontSize: 13 }}>{block.caption}</div> : null}
        </div>
      );
    }
    if (block.type === "link") {
      return (
        <p style={commonTextStyle}>
          <a href={block.url} target="_blank" rel="noopener noreferrer">{block.text || block.title || block.url}</a>
        </p>
      );
    }
    return <p style={commonTextStyle}>{block.text}</p>;
  };

  renderCard = entry => {
    const freshness = this.getFreshnessState(entry);
    const freshnessLabel = freshness === "new"
      ? translateWithFallback("admin_local.guidelines_whats_new_freshness_new", "New")
      : freshness === "changed"
        ? translateWithFallback("admin_local.guidelines_whats_new_freshness_changed", "Modified")
        : translateWithFallback("admin_local.guidelines_whats_new_freshness_stable", "Stable");
    const freshnessColor = freshness === "new" ? "#0a7a3f" : freshness === "changed" ? "#8a5c2a" : "#6a583f";
    const freshnessBackground = freshness === "new" ? "#e3f6eb" : freshness === "changed" ? "#f7e7cf" : "#f0e6d2";
    const isNew = freshness === "new";

    return (
      <div
        key={entry.id}
        style={{
          border: "1px solid #e7dfcf",
          borderRadius: 12,
          padding: 16,
          marginBottom: 14,
          background: "#fffdf8",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <h3 style={{ margin: 0, color: "#3e3527", fontSize: 19, display: "flex", alignItems: "center", gap: 8 }}>
            <span>{entry.title}</span>
            {isNew ? (
              <span
                title={translateWithFallback("admin_local.guidelines_whats_new_freshness_new", "New")}
                style={{ color: "#d88a00", fontSize: 18, lineHeight: 1 }}
              >
                ★
              </span>
            ) : null}
          </h3>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: freshnessColor,
              background: freshnessBackground,
              padding: "5px 9px",
              borderRadius: 999
            }}
          >
            {freshness === "new" ? `★ ${freshnessLabel}` : freshnessLabel}
          </span>
        </div>
        <div style={{ color: "#8a7960", marginTop: 6, fontSize: 13, display: "flex", gap: 14, flexWrap: "wrap" }}>
          <span>
            {translateWithFallback("admin_local.guidelines_whats_new_created_at", "Created")}: {this.formatDate(entry.createdAt)}
          </span>
          <span>
            {translateWithFallback("admin_local.guidelines_whats_new_modified_at", "Modified")}: {this.formatDate(entry.modifiedAt)}
          </span>
        </div>
        {entry.summary ? <p style={{ margin: "10px 0 6px 0", color: "#3e3527" }}>{entry.summary}</p> : null}
        {entry.details ? <p style={{ margin: 0, color: "#665842" }}>{entry.details}</p> : null}
        {entry.contentBlocks.length ? (
          <div style={{ marginTop: 10, borderTop: "1px dashed #e4d8c6", paddingTop: 10 }}>
            {entry.contentBlocks.map(block => (
              <div key={block.id}>{this.renderBlock(block)}</div>
            ))}
          </div>
        ) : null}
        {entry.tags.length ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            {entry.tags.map(tag => {
              const normalizedTag = normalizeTag(tag);
              const isSelected = this.state.moduleFilter.includes(normalizedTag);
              return (
                <span
                  key={tag}
                  style={{
                    background: isSelected ? "#dff3e9" : "#f8f1e4",
                    color: isSelected ? "#195d3a" : "#6f614d",
                    border: isSelected ? "1px solid #9fd0ad" : "1px solid transparent",
                    borderRadius: 999,
                    padding: "4px 8px",
                    fontSize: 12,
                    fontWeight: isSelected ? 700 : 500
                  }}
                >
                  #{this.getTagLabel(tag)}
                </span>
              );
            })}
          </div>
        ) : null}
        {entry.linkLabel && entry.linkUrl ? (
          <div style={{ marginTop: 10 }}>
            <a href={entry.linkUrl} target="_blank" rel="noopener noreferrer">{entry.linkLabel}</a>
          </div>
        ) : null}
      </div>
    );
  };

  componentDidMount() {
    markGuidelinesSeen(this.props.myStatus?.id);
    this.initializeFilterSelects();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.searchTerm !== this.state.searchTerm ||
      prevState.freshnessFilter !== this.state.freshnessFilter ||
      prevState.dateRangeFilter !== this.state.dateRangeFilter ||
      prevState.moduleFilter !== this.state.moduleFilter
    ) {
      this.persistFilterState();
    }

    if (
      prevState.freshnessFilter !== this.state.freshnessFilter ||
      prevState.dateRangeFilter !== this.state.dateRangeFilter
    ) {
      this.initializeFilterSelects();
    }

    if (prevState.moduleFilter !== this.state.moduleFilter) {
      this.syncTagFilterSelects(this.state.moduleFilter);
    }
  }

  syncTagFilterSelects = (selectedValues = []) => {
    if (typeof window === "undefined" || !$) return;

    const nextValues = Array.isArray(selectedValues) ? selectedValues.slice() : [];
    const selects = $(".guidelines-filter-select[multiple]");

    this.isSyncingTagFilterSelect = true;
    try {
      selects.each((_, select) => {
        const $select = $(select);
        const currentValues = $select.val() || [];
        const currentNormalized = Array.isArray(currentValues)
          ? currentValues.map(normalizeTag).filter(Boolean)
          : (currentValues ? [normalizeTag(currentValues)] : []).filter(Boolean);

        if (JSON.stringify(currentNormalized) === JSON.stringify(nextValues)) {
          return;
        }

        if ($select.data("select2")) {
          $select.val(nextValues);
          $select.trigger("change");
        } else {
          $select.val(nextValues);
        }
      });
    } finally {
      this.isSyncingTagFilterSelect = false;
    }
  };

  initializeFilterSelects = () => {
    if (typeof window === "undefined" || !$) return;

    const $selects = $(".guidelines-filter-select");
    $selects.each((_, select) => {
      const $select = $(select);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }

      const isMulti = select.multiple;

      if (!isMulti) {
        return;
      }

      const fallbackPlaceholder = getGuidelinesSelectPlaceholder(true, this.getLanguage());
      $select.select2({
        width: "100%",
        minimumResultsForSearch: 0,
        dropdownAutoWidth: true,
        theme: "default",
        multiple: true,
        allowClear: true,
        closeOnSelect: false,
        placeholder: translateWithFallback("admin_local.guidelines_whats_new_filter_module_placeholder", fallbackPlaceholder),
        matcher: (params, data) => {
          if (!params.term || params.term.trim() === "") {
            return data;
          }

          const term = params.term.toLowerCase();
          const text = data.text ? data.text.toLowerCase() : "";
          return text.includes(term) ? data : null;
        }
      });

      $select.val(this.state.moduleFilter || []);
      $select.off("change.guidelinesTagFilter").on("change.guidelinesTagFilter", (event) => {
        this.syncModuleFilterFromSelect(event.target || $select[0]);
      });
    });
  };

  render() {
    const title = translateWithFallback("admin_local.menus.guidelines_whats_new", "Guidelines & What's new");
    const visibleEntries = this.normalizeEntries().filter(this.entryVisibleForUser);
    const entries = this.filterEntries(visibleEntries);
    const moduleFilterGroups = this.getModuleFilterGroups(entries);
    const tagSummary = this.state.moduleFilter.length > 0
      ? this.getTagSummary(entries).filter(item => this.state.moduleFilter.includes(item.tag)).concat(
          this.getTagSummary(entries).filter(item => !this.state.moduleFilter.includes(item.tag))
        )
      : this.getTagSummary(entries);

    return (
      <>
        <style>{`
          .guidelines-filter-select + .select2-container {
            width: 100% !important;
            min-height: 42px;
          }
          .guidelines-filter-select + .select2-container .select2-selection--single,
          .guidelines-filter-select + .select2-container .select2-selection--multiple {
            min-height: 42px !important;
            height: auto !important;
            border: 1px solid #d6c8b0 !important;
            border-radius: 10px !important;
            box-sizing: border-box;
            box-shadow: 0 1px 2px rgba(90,72,41,0.06);
            background: #fff;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
          }
          .guidelines-filter-select + .select2-container .select2-selection__rendered {
            line-height: 28px !important;
            padding: 4px 30px 4px 12px !important;
            color: #3e3527;
            width: 100%;
          }
          .guidelines-filter-select + .select2-container .select2-selection__arrow {
            height: 40px !important;
          }
          .guidelines-filter-select + .select2-container .select2-selection__clear {
            line-height: 40px !important;
          }
          .guidelines-filter-select + .select2-container .select2-selection__choice {
            background: #eaf8ef !important;
            border: 1px solid #9fd0ad !important;
            color: #1a5d3d !important;
            border-radius: 999px !important;
            padding: 2px 8px !important;
            margin: 4px 4px 4px 0 !important;
          }
        `}</style>
        <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <TopBar {...this.props} />
        <div style={{ flex: "1 1 auto", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        <div style={{ maxWidth: 940, margin: "0 auto", padding: "18px 16px 24px 16px" }}>
          <h2 style={{ marginTop: 0, color: "#3e3527" }}>{title}</h2>
          <p style={{ color: "#6f614d", marginTop: 0 }}>
            {translateWithFallback("admin_local.guidelines_whats_new_intro", "Role-based updates and operational guidelines for your account.")}
          </p>
              <div style={{ border: "1px solid transparent", borderRadius: 12, padding: 12, marginBottom: 12 }}>
            <div style={{ color: "#4d4131", fontWeight: 700, marginBottom: 8 }}>
              {translateWithFallback("admin_local.guidelines_whats_new_structure_title", "Tagged features")}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {tagSummary.length > 0 ? tagSummary.map(({ tag, count }) => {
                const normalizedTag = normalizeTag(tag);
                const isSelected = this.state.moduleFilter.includes(normalizedTag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => this.toggleTagFilter(tag)}
                    style={{
                      background: isSelected ? "#dff3e9" : "#f4ead8",
                      color: "#5f503c",
                      border: isSelected ? "1px solid #98d3ae" : "1px solid #e3d7bf",
                      borderRadius: 999,
                      padding: "5px 10px",
                      fontSize: 12,
                      cursor: "pointer"
                    }}
                  >
                    #{this.getTagLabel(tag)} <span style={{ opacity: 0.8 }}>({count})</span>
                  </button>
                );
              }) : (
                <span style={{ color: "#6f614d" }}>{translateWithFallback("admin_local.guidelines_whats_new_empty", "No tags available.")}</span>
              )}
            </div>
          </div>

          <div
            style={{
                  background: "transparent",
                  border: "1px solid transparent",
              borderRadius: 14,
              padding: 14,
              marginBottom: 14,
              display: "grid",
                  gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 1.5fr)",
              gap: 12,
              alignItems: "center",
              boxShadow: "0 2px 8px rgba(110, 86, 44, 0.06)"
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "span 2" }}>
              <label style={{ fontSize: 11, letterSpacing: 0.4, color: "#7a6a54", textTransform: "uppercase" }}>{translateWithFallback("admin_local.guidelines_whats_new_search_label", "Search")}</label>
              <input
                type="text"
                value={this.state.searchTerm}
                onChange={this.onSearchChange}
                placeholder={translateWithFallback("admin_local.guidelines_whats_new_search_placeholder", "Search title, summary, details or tags")}
                style={{
                  width: "100%",
                  minHeight: 42,
                  border: "1px solid #d9c9a7",
                  borderRadius: 10,
                  padding: "9px 12px",
                  color: "#3e3527",
                  background: "#fff",
                  outline: "none",
                  boxSizing: "border-box",
                  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.03)"
                }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 11, letterSpacing: 0.4, color: "#7a6a54", textTransform: "uppercase" }}>{translateWithFallback("admin_local.guidelines_whats_new_filter_freshness_all", "All updates")}</label>
              <select
                value={this.state.freshnessFilter}
                    data-filter-kind="freshness"
                onChange={this.onFreshnessFilterChange}
                className="guidelines-filter-select"
                    style={{ width: "100%", minWidth: 0, minHeight: 42, border: "1px solid #d6c8b0", borderRadius: 10, padding: "9px 12px", color: "#3e3527", background: "#fff", cursor: "pointer", boxSizing: "border-box", boxShadow: "0 1px 2px rgba(90,72,41,0.06)" }}
              >
                <option value="all">{translateWithFallback("admin_local.guidelines_whats_new_filter_freshness_all", "All updates")}</option>
                <option value="new">{translateWithFallback("admin_local.guidelines_whats_new_freshness_new", "New")}</option>
                <option value="changed">{translateWithFallback("admin_local.guidelines_whats_new_freshness_changed", "Modified")}</option>
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 11, letterSpacing: 0.4, color: "#7a6a54", textTransform: "uppercase" }}>{translateWithFallback("admin_local.guidelines_whats_new_date_range_label", "Date range")}</label>
              <select
                value={this.state.dateRangeFilter}
                    data-filter-kind="date-range"
                onChange={this.onDateRangeFilterChange}
                className="guidelines-filter-select"
                    style={{ width: "100%", minWidth: 0, minHeight: 42, border: "1px solid #d6c8b0", borderRadius: 10, padding: "9px 12px", color: "#3e3527", background: "#fff", cursor: "pointer", boxSizing: "border-box", boxShadow: "0 1px 2px rgba(90,72,41,0.06)" }}
              >
                <option value="all">{this.getDateRangeLabel("all")}</option>
                {FRESHNESS_CONFIG.modifiedSinceOptions.map(option => (
                  <option key={option.value} value={option.value}>{this.getDateRangeLabel(option.value)}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "span 4" }}>
              <label style={{ fontSize: 11, letterSpacing: 0.4, color: "#7a6a54", textTransform: "uppercase" }}>{translateWithFallback("admin_local.guidelines_whats_new_filter_module_all", "Tags")}</label>
              <select
                defaultValue={[]}
                onChange={this.onModuleFilterChange}
                multiple
                className="guidelines-filter-select select2"
                style={{ width: "100%", minHeight: 42, border: "1px solid #d6c8b0", borderRadius: 10, padding: "9px 12px", color: "#3e3527", background: "#fff", cursor: "pointer", boxSizing: "border-box", boxShadow: "0 1px 2px rgba(90,72,41,0.06)" }}
              >
                {moduleFilterGroups.map(group => (
                  group.options.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))
                ))}
              </select>
            </div>
          </div>

          <div style={{ color: "#6f614d", marginBottom: 12 }}>
            {translateWithFallback("admin_local.guidelines_whats_new_results", "Results")}: {entries.length} / {visibleEntries.length}
          </div>

          {entries.length === 0 ? (
            <div style={{ color: "#6f614d", padding: "18px 0" }}>
              {translateWithFallback("admin_local.guidelines_whats_new_empty", "No updates available for your current role.")}
            </div>
          ) : (
            <div>
              {entries.map(this.renderCard)}
            </div>
          )}
        </div>
        </div>
      </div>
      </>
    );
  }
}

const mapStateToProps = state => ({
  myStatus: state.rootReducer.myStatus,
  rbac: state.rootReducer.rbac
});

export default connect(mapStateToProps)(GuidelinesWhatsNew);
